import { ChangeDetectionStrategy, Component, Input, OnChanges } from '@angular/core';
import { isKnownMovieFormatCode, movieFormatCodeKey } from '@digilize/shared/definitions/src';
import { TranslocoModule } from '@jsverse/transloco';

/** One badge: a label key for a code the vocabulary knows, or literal text for the fallback. */
export interface MovieFormatBadge {
  key?: string;
  text?: string;
}

/**
 * The badges for one film format (PAUL-3131): one per code. A code the vocabulary does not know yet - a new
 * Filmdepot label arrives as its own slug - is never dropped: the format's own name stands in for the set.
 * `hide2d` goes by the code, never by `is_main`: a cinema that plays a film only in 3D has 3D as its main.
 */
export const toMovieFormatBadges = (
  codes: string[] | null | undefined,
  name: string | null | undefined,
  hide2d = false
): MovieFormatBadge[] => {
  if (!codes?.length) {
    return name ? [{ text: name }] : [];
  }

  const shown = codes.filter((code) => !(hide2d && code === '2d'));

  if (!shown.length) {
    return [];
  }

  return shown.every(isKnownMovieFormatCode)
    ? shown.map((code) => ({ key: movieFormatCodeKey(code) }))
    : [{ text: name || shown.join(' + ') }];
};

/**
 * A film format as text badges. Shared by the dashboard, the webshop and the user portal, so it stays
 * standalone and free of dashboard dependencies; an icon per code can replace a text badge later.
 */
@Component({
  selector: 'lib-movie-format-badges',
  standalone: true,
  imports: [TranslocoModule],
  templateUrl: './movie-format-badges.component.html',
  styleUrls: ['./movie-format-badges.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MovieFormatBadgesComponent implements OnChanges {
  @Input() codes: string[] | null | undefined;
  /** The format's own name, shown when a code has no label. */
  @Input() name: string | null | undefined;
  /** Leaves out `2d`, so a plain 2D show looks as it always did and the special formats stand out. */
  @Input() hide2d = false;

  badges: MovieFormatBadge[] = [];

  ngOnChanges() {
    this.badges = toMovieFormatBadges(this.codes, this.name, this.hide2d);
  }
}
